import { Bar } from '../reducers/symbolSlice';

export interface TechnicalMetrics {
    volume: {
        current: number;
        average: number;
        isHigh: boolean;
    };
    atr: number;
    sma: {
        period: number;
        value: number;
        deltaPercent: number;
    };
    ema: {
        period: number;
        value: number;
        deltaPercent: number;
    };
    rsi: {
        period: number;
        value: number;
        level: 'oversold' | 'neutral' | 'overbought';
    };
    macd: {
        line: number;
        signal: number;
        histogram: number;
        trend: 'bullish' | 'bearish' | 'neutral';
    };
    bollinger: {
        middle: number;
        upper: number;
        lower: number;
        widthPercent: number;
    };
    score: number;
    scoreMax: number;
    bullishScore: number;
    bearishScore: number;
    netScore: number;
    scoreFactors: Array<{
        key: string;
        label: string;
        passed: boolean;
        points: number;
        side: 'bullish' | 'bearish';
        importance: ScoreFactorImportance;
    }>;
    recommendation: 'strong_buy' | 'buy' | 'hold' | 'sell' | 'strong_sell';
    trailingStopPercent: number;
}

const PORTFOLIO_PERIODS = {
    sma: 50,
    smaLong: 200,
    ema: 50,
    rsi: 14,
};

/** Point weights by signal importance (trend > momentum > RSI/volume > short-term). */
const SCORE_WEIGHTS = {
    trend: 4,
    momentum: 3,
    rsi: 2,
    volume: 2,
    shortTerm: 1,
    risk: 1,
} as const;

const RECOMMENDATION_THRESHOLDS = {
    strongBuy: 7,
    buy: 3,
    hold: -2,
    sell: -5,
} as const;

export type ScoreFactorImportance = 'primary' | 'secondary' | 'tertiary';

const clamp = (value: number, min: number, max: number) => {
    return Math.min(Math.max(value, min), max);
};

export const calculateSMA = (values: number[], period: number): (number | null)[] => {
    const sma: (number | null)[] = [];
    for (let i = 0; i < values.length; i++) {
        if (i < period - 1) {
            sma.push(null);
            continue;
        }

        const avg = values.slice(i - period + 1, i + 1).reduce((sum, v) => sum + v, 0) / period;
        sma.push(avg);
    }

    return sma;
};

export const calculateEMA = (values: number[], period: number): (number | null)[] => {
    const ema: (number | null)[] = [];
    const multiplier = 2 / (period + 1);

    for (let i = 0; i < values.length; i++) {
        if (i < period - 1) {
            ema.push(null);
        } else if (i === period - 1) {
            const smaSeed = values.slice(0, period).reduce((sum, v) => sum + v, 0) / period;
            ema.push(smaSeed);
        } else {
            const prev = ema[i - 1] as number;
            ema.push((values[i] - prev) * multiplier + prev);
        }
    }

    return ema;
};

export const calculateRSI = (values: number[], period: number): (number | null)[] => {
    const rsi: (number | null)[] = [];

    for (let i = 0; i < values.length; i++) {
        if (i < period) {
            rsi.push(null);
            continue;
        }

        let gains = 0;
        let losses = 0;

        for (let j = i - period; j < i; j++) {
            const change = values[j + 1] - values[j];
            if (change >= 0) {
                gains += change;
            } else {
                losses += Math.abs(change);
            }
        }

        if (losses === 0) {
            rsi.push(100);
            continue;
        }

        const rs = gains / losses;
        rsi.push(100 - 100 / (1 + rs));
    }

    return rsi;
};

export const calculateMACD = (values: number[]) => {
    const ema12 = calculateEMA(values, 12);
    const ema26 = calculateEMA(values, 26);

    const macdLine: (number | null)[] = values.map((_, idx) => {
        if (ema12[idx] === null || ema26[idx] === null) {
            return null;
        }
        return (ema12[idx] as number) - (ema26[idx] as number);
    });

    const macdValues = macdLine.map(v => (v === null ? 0 : v));
    const signalLineRaw = calculateEMA(macdValues, 9);
    const signalLine: (number | null)[] = signalLineRaw.map((v, idx) => {
        if (macdLine[idx] === null) {
            return null;
        }
        return v;
    });

    const histogram: (number | null)[] = macdLine.map((line, idx) => {
        const signal = signalLine[idx];
        if (line === null || signal === null) {
            return null;
        }
        return line - signal;
    });

    return { macdLine, signalLine, histogram };
};

export const calculateBollingerBands = (values: number[], period: number = 20, stdDevMultiplier: number = 2) => {
    const middle = calculateSMA(values, period);
    const upper: (number | null)[] = [];
    const lower: (number | null)[] = [];

    for (let i = 0; i < values.length; i++) {
        if (i < period - 1 || middle[i] === null) {
            upper.push(null);
            lower.push(null);
            continue;
        }

        const window = values.slice(i - period + 1, i + 1);
        const mean = middle[i] as number;
        const variance = window.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / period;
        const stdDev = Math.sqrt(variance);

        upper.push(mean + stdDevMultiplier * stdDev);
        lower.push(mean - stdDevMultiplier * stdDev);
    }

    return { middle, upper, lower };
};

export const calculateATR = (bars: Bar[], period: number = 14): number => {
    if (bars.length < 2) {
        return 0;
    }

    const trueRanges: number[] = [];

    for (let i = 1; i < bars.length; i++) {
        const current = bars[i];
        const prevClose = bars[i - 1].c;
        const tr = Math.max(
            current.h - current.l,
            Math.abs(current.h - prevClose),
            Math.abs(current.l - prevClose)
        );
        trueRanges.push(tr);
    }

    const lookback = trueRanges.slice(-period);
    if (!lookback.length) {
        return 0;
    }

    return lookback.reduce((sum, tr) => sum + tr, 0) / lookback.length;
};

export const calculateVolumeStats = (bars: Bar[], period: number = 20) => {
    if (!bars.length) {
        return { current: 0, average: 0, isHigh: false };
    }

    const current = bars[bars.length - 1].v;
    const lookback = bars.slice(-period).map(b => b.v);
    const average = lookback.reduce((sum, v) => sum + v, 0) / Math.max(lookback.length, 1);

    return {
        current,
        average,
        isHigh: current > average * 1.5,
    };
};

export const getRecommendation = (netScore: number): TechnicalMetrics['recommendation'] => {
    if (netScore >= RECOMMENDATION_THRESHOLDS.strongBuy) {
        return 'strong_buy';
    }
    if (netScore >= RECOMMENDATION_THRESHOLDS.buy) {
        return 'buy';
    }
    if (netScore >= RECOMMENDATION_THRESHOLDS.hold) {
        return 'hold';
    }
    if (netScore >= RECOMMENDATION_THRESHOLDS.sell) {
        return 'sell';
    }
    return 'strong_sell';
};

const calculateDynamicTrailingStop = (
    bars: Bar[],
    min: number = 5,
    max: number = 30,
    period: number = 50,
    multiplier: number = 8
): number => {
    if (!bars || bars.length < period + 1) {
        return max;
    }

    const window = bars.slice(-(period + 1));
    let sum = 0;
    for (let i = 1; i < window.length; i++) {
        const prev = window[i - 1].c;
        const curr = window[i].c;
        if (prev !== 0) {
            sum += Math.abs((curr - prev) / prev) * 100;
        }
    }

    const avgChange = sum / period;
    const trailingStop = Math.max(min, Math.min(max, avgChange * multiplier));
    return parseFloat(trailingStop.toFixed(2));
};

// Portfolio model metrics aligned with stockMarketPortfolio.ts
export const buildPortfolioTechnicalMetrics = (bars: Bar[]): TechnicalMetrics | null => {
    if (!bars.length) {
        return null;
    }

    const closePrices = bars.map(b => b.c);
    const currentPrice = closePrices[closePrices.length - 1] || 0;

    const sma50Series = calculateSMA(closePrices, PORTFOLIO_PERIODS.sma);
    const sma200Series = calculateSMA(closePrices, PORTFOLIO_PERIODS.smaLong);
    const ema50Series = calculateEMA(closePrices, PORTFOLIO_PERIODS.ema);
    const rsiSeries = calculateRSI(closePrices, PORTFOLIO_PERIODS.rsi);
    const { macdLine, signalLine, histogram } = calculateMACD(closePrices);
    const bollinger = calculateBollingerBands(closePrices, 20, 2);
    const atr = calculateATR(bars, 14);
    const volume = calculateVolumeStats(bars, 20);

    const sma50 = sma50Series[sma50Series.length - 1] ?? currentPrice;
    const ema50 = ema50Series[ema50Series.length - 1] ?? currentPrice;
    const rsiValue = rsiSeries[rsiSeries.length - 1] ?? 50;
    const macdValue = macdLine[macdLine.length - 1] ?? 0;
    const signalValue = signalLine[signalLine.length - 1] ?? 0;
    const histogramValue = histogram[histogram.length - 1] ?? 0;
    const bollingerMiddle = bollinger.middle[bollinger.middle.length - 1] ?? currentPrice;
    const bollingerUpper = bollinger.upper[bollinger.upper.length - 1] ?? currentPrice;
    const bollingerLower = bollinger.lower[bollinger.lower.length - 1] ?? currentPrice;

    const previous = bars.length > 1 ? bars[bars.length - 2].c : currentPrice;
    const isUpDay = currentPrice > previous;
    const isDownDay = currentPrice < previous;
    const dailyChangePercent = previous !== 0 ? Math.abs(((currentPrice - previous) / previous) * 100) : 0;
    const recentBars = bars.slice(-10);
    const upDays = recentBars.reduce((count, bar, idx) => {
        if (idx === 0) {
            return count;
        }
        return bar.c > recentBars[idx - 1].c ? count + 1 : count;
    }, 0);
    const downDays = recentBars.reduce((count, bar, idx) => {
        if (idx === 0) {
            return count;
        }
        return bar.c < recentBars[idx - 1].c ? count + 1 : count;
    }, 0);

    const sma200Valid = sma200Series[sma200Series.length - 1] !== null;
    const sma200Value = sma200Series[sma200Series.length - 1] as number | null;
    const rsiHealthy = rsiValue >= 40 && rsiValue <= 70;

    const scoreFactors: TechnicalMetrics['scoreFactors'] = [
        {
            key: 'sma_golden_cross',
            label: 'SMA 50 above SMA 200 (golden cross)',
            passed: sma200Valid && sma200Value !== null && sma50 > sma200Value,
            points: SCORE_WEIGHTS.trend,
            side: 'bullish',
            importance: 'primary',
        },
        {
            key: 'macd_bullish',
            label: 'MACD above Signal',
            passed: macdValue > signalValue,
            points: SCORE_WEIGHTS.momentum,
            side: 'bullish',
            importance: 'primary',
        },
        {
            key: 'rsi_healthy',
            label: 'RSI 14 in healthy zone (40–70)',
            passed: rsiHealthy,
            points: SCORE_WEIGHTS.rsi,
            side: 'bullish',
            importance: 'secondary',
        },
        {
            key: 'volume_up_day',
            label: 'High volume on up day (>1.5x avg)',
            passed: volume.isHigh && isUpDay,
            points: SCORE_WEIGHTS.volume,
            side: 'bullish',
            importance: 'secondary',
        },
        {
            key: 'uptrend_days',
            label: 'At least 3 up candles (last 10)',
            passed: upDays >= 3,
            points: SCORE_WEIGHTS.shortTerm,
            side: 'bullish',
            importance: 'tertiary',
        },
        {
            key: 'volatility_limit',
            label: 'Daily move below 30%',
            passed: dailyChangePercent < 30,
            points: SCORE_WEIGHTS.risk,
            side: 'bullish',
            importance: 'tertiary',
        },
        {
            key: 'sma_death_cross',
            label: 'SMA 50 below SMA 200 (death cross)',
            passed: sma200Valid && sma200Value !== null && sma50 < sma200Value,
            points: SCORE_WEIGHTS.trend,
            side: 'bearish',
            importance: 'primary',
        },
        {
            key: 'macd_bearish',
            label: 'MACD below Signal',
            passed: macdValue < signalValue,
            points: SCORE_WEIGHTS.momentum,
            side: 'bearish',
            importance: 'primary',
        },
        {
            key: 'rsi_overbought',
            label: 'RSI 14 overbought (>70)',
            passed: rsiValue > 70,
            points: SCORE_WEIGHTS.rsi,
            side: 'bearish',
            importance: 'secondary',
        },
        {
            key: 'volume_down_day',
            label: 'High volume on down day (>1.5x avg)',
            passed: volume.isHigh && isDownDay,
            points: SCORE_WEIGHTS.volume,
            side: 'bearish',
            importance: 'secondary',
        },
        {
            key: 'rsi_weak',
            label: 'RSI 14 weak momentum (<40)',
            passed: rsiValue < 40,
            points: SCORE_WEIGHTS.shortTerm,
            side: 'bearish',
            importance: 'tertiary',
        },
        {
            key: 'downtrend_days',
            label: 'At least 3 down candles (last 10)',
            passed: downDays >= 3,
            points: SCORE_WEIGHTS.shortTerm,
            side: 'bearish',
            importance: 'tertiary',
        },
    ];

    const bullishScore = scoreFactors
        .filter((factor) => factor.side === 'bullish')
        .reduce((sum, factor) => sum + (factor.passed ? factor.points : 0), 0);
    const bearishScore = scoreFactors
        .filter((factor) => factor.side === 'bearish')
        .reduce((sum, factor) => sum + (factor.passed ? factor.points : 0), 0);
    const netScore = bullishScore - bearishScore;
    const scoreMax = scoreFactors.reduce((sum, factor) => sum + factor.points, 0);

    const rsiLevel: TechnicalMetrics['rsi']['level'] = rsiValue < 30 ? 'oversold' : rsiValue > 70 ? 'overbought' : 'neutral';
    const macdTrend: TechnicalMetrics['macd']['trend'] = histogramValue > 0 ? 'bullish' : histogramValue < 0 ? 'bearish' : 'neutral';
    const bollingerWidthPercent = bollingerMiddle !== 0 ? ((bollingerUpper - bollingerLower) / bollingerMiddle) * 100 : 0;
    const smaDeltaPercent = sma50 !== 0 ? ((currentPrice - sma50) / sma50) * 100 : 0;
    const emaDeltaPercent = ema50 !== 0 ? ((currentPrice - ema50) / ema50) * 100 : 0;

    return {
        volume,
        atr,
        sma: {
            period: PORTFOLIO_PERIODS.sma,
            value: sma50,
            deltaPercent: smaDeltaPercent,
        },
        ema: {
            period: PORTFOLIO_PERIODS.ema,
            value: ema50,
            deltaPercent: emaDeltaPercent,
        },
        rsi: {
            period: PORTFOLIO_PERIODS.rsi,
            value: rsiValue,
            level: rsiLevel,
        },
        macd: {
            line: macdValue,
            signal: signalValue,
            histogram: histogramValue,
            trend: macdTrend,
        },
        bollinger: {
            middle: bollingerMiddle,
            upper: bollingerUpper,
            lower: bollingerLower,
            widthPercent: bollingerWidthPercent,
        },
        score: netScore,
        scoreMax,
        bullishScore,
        bearishScore,
        netScore,
        scoreFactors,
        recommendation: getRecommendation(netScore),
        trailingStopPercent: calculateDynamicTrailingStop(bars, 5, 30, 50, 8),
    };
};
